import { DatePicker, type DatePickerProps } from "@mui/x-date-pickers-pro";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

export type ReactHookDatePickerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  datePickerProps?: Partial<DatePickerProps<any>>;
  name: TName;
  label: string;
  required?: boolean;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  control?: Control<TFieldValues> | undefined;
  disabled?: boolean;
};

/**
 * @see {@link https://react-hook-form.com/api/usecontroller/controller| react-hook-form Controller}
 * @see {@link https://mui.com/x/react-date-pickers/getting-started| MUI DatePicker}
 */
export function ReactHookDatePicker<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  required,
  datePickerProps,
  rules,
  control,
  disabled,
}: ReactHookDatePickerProps<TFieldValues, TName>) {
  const { t } = useTranslation();
  const { t: componentT } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:ReactHookDatePicker",
  });
  const invalidDateErrorMessage = componentT("invalidDateErrorMessage");
  const requiredErrorMessage = t("Next:Core:FieldRequiredValidationMessage");

  return (
    <Controller
      name={name}
      rules={{
        required: required ? requiredErrorMessage : undefined,
        ...rules,
        validate: {
          ...rules?.validate,
          isValidDate: (value) => {
            if (!required) {
              return true;
            }
            if (
              typeof value === "object" &&
              value !== null &&
              (value as any) instanceof Date &&
              !Number.isNaN((value as Date).getTime())
            ) {
              return true;
            }
            return invalidDateErrorMessage;
          },
        },
      }}
      control={control}
      render={({ field: { ref: inputRef, ...field }, fieldState }) => (
        <DatePicker
          label={`${required ? "* " : ""} ${label}`}
          {...field}
          {...datePickerProps}
          inputRef={inputRef}
          disabled={disabled}
          slotProps={{
            ...datePickerProps?.slotProps,
            textField: {
              ...datePickerProps?.slotProps?.textField,
              error: Boolean(fieldState.error),
              helperText: fieldState.error?.message && fieldState.error.message,
            },
          }}
        />
      )}
    />
  );
}
